import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } from "@solana/web3.js";
import { 
  getAssociatedTokenAddress, 
  createAssociatedTokenAccountInstruction,
  TOKEN_PROGRAM_ID 
} from "@solana/spl-token";
import * as anchor from "@coral-xyz/anchor";

// Update this after deploying your contract
export const SWAP_PROGRAM_ID = new PublicKey(
  process.env.NEXT_PUBLIC_SWAP_PROGRAM_ID || "6kQyAWq3nSJxQr43R7EALhKw95v8VrRi7kYnV4DXVUTZ"
);

export const IRMA_MINT = new PublicKey(
  process.env.NEXT_PUBLIC_IRMA_MINT || "Ap1GpUh9UbujzpchTu3iCqABdHEzxWBNu4pPAHEqPvLR"
);

export const SOL_MINT = new PublicKey("So11111111111111111111111111111111111111112");
export const USDC_MINT = new PublicKey(
  process.env.NEXT_PUBLIC_USDC_MINT || "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v"
);
export const USDT_MINT = new PublicKey(
  process.env.NEXT_PUBLIC_USDT_MINT || "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB"
);

// Rate: 1 SOL = 1000 IRMA
export const IRMA_PER_SOL = 1000;
export const FEE_BPS = 100; // 1% fee

export const TOKEN_DECIMALS: Record<string, number> = {
  SOL: 9,
  IRMA: 9,
  USDC: 6,
  USDT: 6,
};

export function getTokenMint(token: string): PublicKey {
  switch (token) {
    case "SOL": return SOL_MINT;
    case "IRMA": return IRMA_MINT;
    case "USDC": return USDC_MINT;
    case "USDT": return USDT_MINT;
    default: throw new Error(`Unknown token: ${token}`);
  }
}

export function getPoolPDA(irmaMint: PublicKey): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from("pool"), irmaMint.toBuffer()],
    SWAP_PROGRAM_ID
  );
}

export interface SwapQuote {
  inputAmount: number;
  outputAmount: number;
  fee: number;
  rate: number;
  priceImpact: number;
}

export function getSwapQuote(
  inputToken: "SOL" | "IRMA",
  inputAmount: number
): SwapQuote {
  const irmaPerSol = IRMA_PER_SOL;
  let outputAmount: number;
  let fee: number;

  if (inputToken === "SOL") {
    // SOL -> IRMA
    const rawOutput = inputAmount * irmaPerSol;
    fee = rawOutput * (FEE_BPS / 10000);
    outputAmount = rawOutput - fee;
  } else {
    // IRMA -> SOL
    const rawOutput = inputAmount / irmaPerSol;
    fee = rawOutput * (FEE_BPS / 10000);
    outputAmount = rawOutput - fee;
  }

  return {
    inputAmount,
    outputAmount,
    fee,
    rate: irmaPerSol,
    priceImpact: 0,
  };
}

// Check if pool is initialized
export async function isPoolInitialized(
  connection: Connection,
  irmaMint: PublicKey = IRMA_MINT
): Promise<boolean> {
  const [poolPDA] = getPoolPDA(irmaMint);
  const accountInfo = await connection.getAccountInfo(poolPDA);
  return accountInfo !== null;
}

// Initialize pool instruction
export function createInitializePoolInstruction(
  authority: PublicKey,
  irmaMint: PublicKey,
  feeBps: number = FEE_BPS
): anchor.web3.TransactionInstruction {
  const [poolPDA] = getPoolPDA(irmaMint);

  // Discriminator: [175, 175, 109, 31, 13, 152, 155, 237]
  return new anchor.web3.TransactionInstruction({
    keys: [
      { pubkey: authority, isSigner: true, isWritable: true },
      { pubkey: irmaMint, isSigner: false, isWritable: false },
      { pubkey: poolPDA, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
    programId: SWAP_PROGRAM_ID,
    data: Buffer.concat([
      Buffer.from([175, 175, 109, 31, 13, 152, 155, 237]),
      Buffer.from(new Uint8Array(new Uint16Array([feeBps]).buffer)),
    ]),
  });
}

export async function createSwapSolForIrmaTransaction(
  connection: Connection,
  userPublicKey: PublicKey,
  solAmount: number,
  irmaMint: PublicKey = IRMA_MINT
): Promise<Transaction> {
  const [poolPDA] = getPoolPDA(irmaMint);
  const userIrmaAta = await getAssociatedTokenAddress(irmaMint, userPublicKey);
  
  const transaction = new Transaction();

  // Check if pool is initialized
  const poolInitialized = await isPoolInitialized(connection, irmaMint);
  if (!poolInitialized) {
    console.log("Pool not initialized, adding initialize instruction...");
    transaction.add(createInitializePoolInstruction(userPublicKey, irmaMint));
  }
  
  // Check if user has IRMA token account
  const ataInfo = await connection.getAccountInfo(userIrmaAta);
  if (!ataInfo) {
    transaction.add(
      createAssociatedTokenAccountInstruction(
        userPublicKey,
        userIrmaAta,
        userPublicKey,
        irmaMint
      )
    );
  }
  
  const lamports = Math.floor(solAmount * LAMPORTS_PER_SOL);
  
  // Discriminator: [6, 33, 47, 146, 32, 222, 81, 245]
  // Accounts: user, pool, irma_mint, user_irma_account, system_program, token_program
  const swapIx = new anchor.web3.TransactionInstruction({
    keys: [
      { pubkey: userPublicKey, isSigner: true, isWritable: true },
      { pubkey: poolPDA, isSigner: false, isWritable: true },
      { pubkey: irmaMint, isSigner: false, isWritable: true },
      { pubkey: userIrmaAta, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    ],
    programId: SWAP_PROGRAM_ID,
    data: Buffer.concat([
      Buffer.from([6, 33, 47, 146, 32, 222, 81, 245]),
      Buffer.from(new anchor.BN(lamports).toArray("le", 8)),
    ]),
  });
  
  transaction.add(swapIx);
  
  const { blockhash } = await connection.getLatestBlockhash();
  transaction.recentBlockhash = blockhash;
  transaction.feePayer = userPublicKey;
  
  return transaction;
}

export async function createSwapIrmaForSolTransaction(
  connection: Connection,
  userPublicKey: PublicKey,
  irmaAmount: number,
  irmaMint: PublicKey = IRMA_MINT
): Promise<Transaction> {
  const [poolPDA] = getPoolPDA(irmaMint);
  const userIrmaAta = await getAssociatedTokenAddress(irmaMint, userPublicKey);

  const transaction = new Transaction();

  const rawAmount = Math.floor(irmaAmount * 1_000_000_000);

  // Discriminator: [243, 215, 160, 144, 191, 43, 30, 193]
  // Accounts: user, pool, irma_mint, user_irma_account, token_program
  const swapIx = new anchor.web3.TransactionInstruction({
    keys: [
      { pubkey: userPublicKey, isSigner: true, isWritable: true },
      { pubkey: poolPDA, isSigner: false, isWritable: true },
      { pubkey: irmaMint, isSigner: false, isWritable: true },
      { pubkey: userIrmaAta, isSigner: false, isWritable: true },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    ],
    programId: SWAP_PROGRAM_ID,
    data: Buffer.concat([
      Buffer.from([243, 215, 160, 144, 191, 43, 30, 193]),
      Buffer.from(new anchor.BN(rawAmount).toArray("le", 8)),
    ]),
  });

  transaction.add(swapIx);

  const { blockhash } = await connection.getLatestBlockhash();
  transaction.recentBlockhash = blockhash;
  transaction.feePayer = userPublicKey;

  return transaction;
}

// ── Routed Swaps (USDC/USDT ↔ IRMA via SOL intermediate) ──

export interface RoutedSwapQuote extends SwapQuote {
  route: "token-to-irma" | "irma-to-token";
  intermediarySolAmount: number;
  jupiterQuote: SwapQuote;
  contractQuote: SwapQuote;
}

export function isRoutedSwap(from: string, to: string): boolean {
  return (
    ((from === "USDC" || from === "USDT") && to === "IRMA") ||
    (from === "IRMA" && (to === "USDC" || to === "USDT"))
  );
}

export async function getRoutedQuote(
  fromToken: string,
  toToken: string,
  amount: number
): Promise<RoutedSwapQuote> {
  if ((fromToken === "USDC" || fromToken === "USDT") && toToken === "IRMA") {
    // Step 1: token → SOL (Jupiter quote)
    const jupQuote = await getJupiterQuote(fromToken, "SOL", amount);
    // Step 2: SOL → IRMA (contract rate)
    const contractQuote = getSwapQuote("SOL", jupQuote.outputAmount);

    return {
      inputAmount: amount,
      outputAmount: contractQuote.outputAmount,
      fee: contractQuote.fee,
      rate: amount > 0 ? contractQuote.outputAmount / amount : 0,
      priceImpact: jupQuote.priceImpact,
      route: "token-to-irma",
      intermediarySolAmount: jupQuote.outputAmount,
      jupiterQuote: jupQuote,
      contractQuote,
    };
  } else {
    // IRMA → USDC/USDT
    // Step 1: IRMA → SOL (contract rate)
    const contractQuote = getSwapQuote("IRMA", amount);
    // Step 2: SOL → token (Jupiter quote)
    const jupQuote = await getJupiterQuote("SOL", toToken, contractQuote.outputAmount);

    return {
      inputAmount: amount,
      outputAmount: jupQuote.outputAmount,
      fee: contractQuote.fee,
      rate: amount > 0 ? jupQuote.outputAmount / amount : 0,
      priceImpact: jupQuote.priceImpact,
      route: "irma-to-token",
      intermediarySolAmount: contractQuote.outputAmount,
      jupiterQuote: jupQuote,
      contractQuote,
    };
  }
}

// ── Jupiter Integration (for USDC/USDT swaps) ──

export async function getJupiterQuote(
  inputToken: string,
  outputToken: string,
  amount: number
): Promise<SwapQuote> {
  const inputMint = getTokenMint(inputToken);
  const outputMint = getTokenMint(outputToken);
  const decimals = TOKEN_DECIMALS[inputToken];
  const rawAmount = Math.floor(amount * Math.pow(10, decimals));

  const url = `https://quote-api.jup.ag/v6/quote?inputMint=${inputMint.toString()}&outputMint=${outputMint.toString()}&amount=${rawAmount}&slippageBps=50`;
  const response = await fetch(url);
  if (!response.ok) throw new Error("Failed to get quote from Jupiter");
  const data = await response.json();

  const outDecimals = TOKEN_DECIMALS[outputToken];
  const outputAmount = parseInt(data.outAmount) / Math.pow(10, outDecimals);
  const inputParsed = parseInt(data.inAmount) / Math.pow(10, decimals);

  return {
    inputAmount: inputParsed,
    outputAmount,
    fee: 0,
    rate: inputParsed > 0 ? outputAmount / inputParsed : 0,
    priceImpact: parseFloat(data.priceImpactPct || "0"),
  };
}

export async function createJupiterSwapTransaction(
  inputToken: string,
  outputToken: string,
  amount: number,
  userPublicKey: PublicKey
): Promise<Uint8Array> {
  const inputMint = getTokenMint(inputToken);
  const outputMint = getTokenMint(outputToken);
  const decimals = TOKEN_DECIMALS[inputToken];
  const rawAmount = Math.floor(amount * Math.pow(10, decimals));

  // Get quote
  const quoteUrl = `https://quote-api.jup.ag/v6/quote?inputMint=${inputMint.toString()}&outputMint=${outputMint.toString()}&amount=${rawAmount}&slippageBps=50`;
  const quoteResponse = await fetch(quoteUrl);
  if (!quoteResponse.ok) throw new Error("Failed to get Jupiter quote");
  const quoteData = await quoteResponse.json();

  // Get swap transaction
  const swapResponse = await fetch("https://quote-api.jup.ag/v6/swap", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      quoteResponse: quoteData,
      userPublicKey: userPublicKey.toString(),
      wrapAndUnwrapSol: true,
    }),
  });
  if (!swapResponse.ok) throw new Error("Failed to create Jupiter swap transaction");
  const { swapTransaction } = await swapResponse.json();

  return Buffer.from(swapTransaction, "base64");
}
